import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Building2, ChevronRight, FolderKanban, Package, Search, SlidersHorizontal, Users, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/app/PageHeader';
import { Panel, PanelHeader } from '@/components/app/States';
import { Note } from '@/components/app/Badges';
import { ExampleDataNote, useDriftRefs, useIsDesktop } from '@/components/app/DriftUI';
import { AdminRegister, matchesSearch } from '@/components/app/AdminRegister';
import { AdminMaskiner } from '@/components/app/AdminMaskiner';
import { AdminFirma } from '@/components/app/AdminFirma';
import { ADMIN_REGISTER_LABELS, ADMIN_SECTIONS, adminListMachines, adminListRegister, adminOverview } from '@/lib/driftApi';
import { cn } from '@/lib/utils';

/*
  Innstillinger: ett ryddig område med søk og fem seksjoner. Registrene her ER referansedataene som Timer, Vareforbruk,
  Maskiner, RUH/kvalitet og rapporter bruker – ingen parallelle lister. Arkivering fremfor sletting; historikk før → etter.
  Ingen publisering, varsler, invitasjoner, integrasjoner eller ekte tilgangsendringer.
*/

const ICONS = { medarbeidere: Users, 'kunder-prosjekter': FolderKanban, registreringsoppsett: SlidersHorizontal, 'varer-maskiner': Package, 'firma-rutiner': Building2 };

function sectionCounts(section, overview) {
  if (!overview) return null;
  const regs = overview.registers.filter((r) => section.registers.includes(r.register));
  if (!regs.length) return null;
  const active = regs.reduce((a, r) => a + r.active, 0);
  const total = regs.reduce((a, r) => a + r.total, 0);
  return { active, inactive: total - active };
}

/** Søk på tvers av alle registre (lastes først når noen søker fra oversikten; hentes én gang per visning av oversikten). */
function useGlobalSearch(q, refs) {
  const [all, setAll] = useState(null);
  const [loading, setLoading] = useState(false);
  const started = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!q || started.current) return;
    started.current = true;
    setLoading(true);
    const names = ADMIN_SECTIONS.flatMap((s) => s.registers).filter((n) => n !== 'machines' && n !== 'company');
    Promise.all([...names.map((n) => adminListRegister(n).then((d) => ({ name: n, fields: d.fields, items: d.items }))), adminListMachines().then((d) => ({ name: 'machines', fields: {}, items: d.machines.map((m) => ({ ...m, id: m.machineId, name: `${m.internalNo} · ${m.name}` })) }))])
      .then((lists) => mounted.current && setAll(lists))
      .catch(() => mounted.current && setAll([]))
      .finally(() => mounted.current && setLoading(false));
  }, [q]);
  const results = useMemo(() => {
    if (!q || !all) return [];
    return all
      .map((l) => ({ ...l, hits: l.items.filter((x) => matchesSearch(x, q, l.fields, refs)) }))
      .filter((l) => l.hits.length);
  }, [q, all, refs]);
  return { results, loading: loading && !all };
}

function SectionNav({ current, overview, onPick }) {
  return (
    <nav aria-label="Seksjoner" className="flex min-w-0 flex-wrap gap-1.5 lg:flex-col lg:gap-0.5" data-testid="settings-sections">
      {ADMIN_SECTIONS.map((s) => {
        const Icon = ICONS[s.id];
        const c = sectionCounts(s, overview);
        const active = current === s.id;
        return (
          <button key={s.id} type="button" onClick={() => onPick(s.id)} aria-current={active ? 'page' : undefined} className={cn('flex h-9 items-center gap-2 rounded-[8px] px-2.5 text-left text-[14px] font-medium transition-[background-color,color,box-shadow] duration-150 hover:bg-[rgba(31,33,48,0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] lg:w-full', active ? 'bg-white text-[var(--hr-heading)] shadow-[0_1px_2px_rgba(16,24,40,0.06)] ring-1 ring-[var(--hr-hairline)] max-lg:bg-[var(--hr-heading)] max-lg:text-white max-lg:ring-0' : 'text-[var(--hr-text)] max-lg:border max-lg:border-[var(--hr-border)]')} data-testid={`settings-section-${s.id}`}>
            <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-[var(--hr-heading)] max-lg:text-white' : 'text-[var(--hr-muted)]')} aria-hidden="true" />
            <span className="truncate">{s.label}</span>
            {c && <span className="hr-tabular ml-auto hidden pl-2 text-[12px] text-[var(--hr-muted-2)] lg:inline">{c.active}</span>}
          </button>
        );
      })}
    </nav>
  );
}

function Overview({ overview, error, onRetry, q, refs, onOpen }) {
  const { results, loading } = useGlobalSearch(q, refs);
  if (q) {
    return (
      <Panel data-testid="settings-search-results">
        <PanelHeader title={`Søk: «${q}»`} description={loading ? 'Søker i alle registre …' : `${results.reduce((a, l) => a + l.hits.length, 0)} treff i ${results.length} ${results.length === 1 ? 'register' : 'registre'}`} />
        {!loading && results.length === 0 && <div className="px-5 py-8 text-center text-[14px] text-[var(--hr-muted)]">Ingen treff. Prøv kortere søkeord – det søkes i nummer, navn, kode, kunde, sted og kategorier.</div>}
        {results.map((l) => (
          <div key={l.name} className="border-t border-[var(--hr-hairline)] first:border-t-0">
            <div className="px-5 pb-1 pt-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">{ADMIN_REGISTER_LABELS[l.name]}</div>
            <ul className="pb-2">
              {l.hits.slice(0, 8).map((x) => (
                <li key={x.id}>
                  <button type="button" onClick={() => onOpen(l.name, x.id)} className="flex w-full items-center gap-3 px-5 py-1.5 text-left text-[14px] transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]" data-testid={`settings-search-hit-${l.name}-${x.id}`}>
                    <span className="hr-tabular w-[80px] shrink-0 truncate text-[13px] text-[var(--hr-muted)]">{x.code ?? x.id}</span>
                    <span className={cn('min-w-0 flex-1 truncate', x.active === false || x.status === 'avsluttet' ? 'text-[var(--hr-muted)]' : 'text-[var(--hr-heading)]')}>{x.name ?? x.label}</span>
                    {(x.active === false || x.status === 'avsluttet') && <span className="text-[12px] text-[var(--hr-muted-2)]">{x.status === 'avsluttet' ? 'avsluttet' : 'arkivert'}</span>}
                    <ChevronRight className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
                  </button>
                </li>
              ))}
              {l.hits.length > 8 && <li className="px-5 py-1 text-[13px] text-[var(--hr-muted-2)]">+ {l.hits.length - 8} til – åpne registeret for å se alle.</li>}
            </ul>
          </div>
        ))}
      </Panel>
    );
  }
  return (
    <Panel data-testid="settings-overview">
      <PanelHeader title="Registre" description="Opprett eller endre her – valget dukker opp i registreringene med én gang, og rapportene bruker samme ID-er." />
      {error && !overview && (
        <div className="p-4">
          <Note tone="amber" role="alert" data-testid="settings-overview-error">
            Kunne ikke hente oversikten. <button type="button" onClick={onRetry} className="font-medium underline underline-offset-2" data-testid="settings-overview-retry">Prøv igjen</button>
          </Note>
        </div>
      )}
      <ul className="divide-y divide-[var(--hr-hairline)]">
        {ADMIN_SECTIONS.map((s) => {
          const Icon = ICONS[s.id];
          const c = sectionCounts(s, overview);
          return (
            <li key={s.id}>
              <Link to={`/innstillinger/${s.id}`} className="group flex items-center gap-3 px-4 py-3 transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)] sm:px-5" data-testid={`settings-overview-${s.id}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[var(--hr-surface-2)] text-[var(--hr-muted)] group-hover:text-[var(--hr-heading)]">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-semibold text-[var(--hr-heading)]">{s.label}</span>
                  <span className="block truncate text-[13px] text-[var(--hr-muted)]">{s.description}</span>
                </span>
                <span className="hidden shrink-0 text-right text-[13px] text-[var(--hr-muted-2)] sm:block" data-testid={`settings-overview-count-${s.id}`}>
                  {c ? <>{c.active} aktive{c.inactive ? ` · ${c.inactive} arkiverte` : ''}</> : s.id === 'firma-rutiner' && overview ? `${overview.company?.routines?.length ?? 0} rutiner` : ''}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
              </Link>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

export default function InnstillingerPage() {
  const { seksjon } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { refs } = useDriftRefs();
  const desktop = useIsDesktop();
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState(null);
  const [q, setQ] = useState(params.get('q') ?? '');
  const section = ADMIN_SECTIONS.find((s) => s.id === seksjon) ?? null;
  const register = section ? (section.registers.includes(params.get('register')) ? params.get('register') : section.registers[0]) : null;
  const openId = params.get('post');

  const loadOverview = useCallback(() => {
    adminOverview()
      .then((d) => {
        setOverview(d);
        setOverviewError(null);
      })
      .catch((e) => setOverviewError(e));
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    document.title = `${section ? `${section.label} · ` : ''}Innstillinger · Hardanger Rock`;
  }, [section]);

  // Søket speiles til URL-en (q) så visningen kan deles; tømmes med X. Effekten kjører etter at ny plassering er
  // committet, så den aldri skriver over en navigasjon (derfor ingen setQ i samme hendelse som navigate).
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set('q', q);
    else next.delete('q');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [q, params, setParams]);

  const pickSection = (id) => navigate(`/innstillinger/${id}${q ? `?q=${encodeURIComponent(q)}` : ''}`);
  const pickRegister = (name) => {
    const next = new URLSearchParams(params);
    next.set('register', name);
    next.delete('post');
    setParams(next, { replace: true });
  };
  const openHit = (name, id) => {
    const s = ADMIN_SECTIONS.find((x) => x.registers.includes(name));
    navigate(`/innstillinger/${s.id}?register=${name}&post=${encodeURIComponent(id)}`);
  };
  const onLoaded = useCallback(() => loadOverview(), [loadOverview]);

  return (
    <div className="space-y-5" data-testid="settings-page">
      <PageHeader
        title="Innstillinger"
        lead="Registrene som driver timer, vareforbruk, maskiner, RUH/kvalitet og rapporter. Arkiver i stedet for å slette – historikken består."
        breadcrumbs={section && !desktop ? [{ label: 'Innstillinger', to: '/innstillinger', testId: 'settings-crumb-root' }, { label: section.label }] : undefined}
      >
        <div className="relative w-full sm:w-[320px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--hr-muted-2)]" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={section ? `Søk i ${section.label.toLowerCase()}` : 'Søk i alle registre'} aria-label="Søk i innstillinger" className="h-10 rounded-[10px] border-[var(--hr-border)] bg-white pl-9 pr-9 text-[14px] shadow-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid="settings-search" />
          {q && (
            <button type="button" onClick={() => setQ('')} aria-label="Tøm søk" className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-[var(--hr-muted)] hover:bg-[var(--hr-surface-2)] hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid="settings-search-clear">
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
      </PageHeader>

      <ExampleDataNote details="Alle poster er fiktive og kan endres fritt. Demonstrasjonsroller styrer ikke pålogging eller ekte tilgang – det krever eget oppsett. Satser, overtid, diett, fakturering og godkjenningsregler er ikke konfigurert. Ingen koblinger til SmartDok, telematikk eller andre systemer; ingen meldinger sendes.">
        Endringer her slår rett inn i skjemaene og rapportene.
      </ExampleDataNote>

      <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-6">
        <SectionNav current={section?.id} overview={overview} onPick={pickSection} />
        <div className="min-w-0 space-y-4">
          {!section && <Overview overview={overview} error={overviewError} onRetry={loadOverview} q={q} refs={refs} onOpen={openHit} />}
          {section && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <p className="max-w-[720px] text-[14px] leading-5 text-[var(--hr-muted)]" data-testid="settings-section-lead">{section.description}</p>
                {section.registers.length > 1 && (
                  <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Register" data-testid="settings-register-tabs">
                    {section.registers.map((name) => (
                      <button key={name} type="button" role="tab" aria-selected={register === name} onClick={() => pickRegister(name)} className={cn('h-8 rounded-full border px-3 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', register === name ? 'border-[var(--hr-heading)] bg-[var(--hr-heading)] text-white' : 'border-[var(--hr-border)] bg-white text-[var(--hr-text)] hover:bg-[var(--hr-surface-2)]')} data-testid={`settings-register-tab-${name}`}>
                        {ADMIN_REGISTER_LABELS[name]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {register === 'company' ? <AdminFirma onLoaded={onLoaded} /> : register === 'machines' ? <AdminMaskiner refs={refs} search={q} onLoaded={onLoaded} /> : <AdminRegister key={register} name={register} refs={refs} search={q} openId={openId} onLoaded={onLoaded} />}
              {section.id === 'medarbeidere' && (
                <p className="text-[13px] leading-5 text-[var(--hr-muted-2)]" data-testid="settings-employees-note">
                  Demonstrasjonsrollen er kun visning. Ekte tilgangsstyring, invitasjoner og brukerkontoer krever eget oppsett og finnes ikke i demoen. Ingen fødselsnummer, bankdata eller private opplysninger lagres.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
